import { ScrollView, Pressable } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@visioni/a3s-ui/components";
import { Icon } from "@visioni/a3s-ui/components";
import { NotificationItem } from "./NotificationItem";
import type { ActivityItem } from "./types";

export interface NotificationSidePanelProps {
  activities: ActivityItem[];
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onActivityPress?: (activity: ActivityItem) => void;
  onNavigate?: (activity: ActivityItem) => void;
  onMarkAllRead?: () => void;
  onClose?: () => void;
  onLoadMore?: () => void;
}

export function NotificationSidePanel({
  activities,
  hasMore,
  isLoadingMore,
  onActivityPress,
  onNavigate,
  onMarkAllRead,
  onClose,
  onLoadMore,
}: NotificationSidePanelProps) {
  const { theme } = useUnistyles();
  const unreadCount = activities.filter((a) => !a.read).length;

  return (
    <View flex={1} height="100%">
      <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="6xl">
        <View flexDirection="row" alignItems="center" gap="md">
          <Text textStyle="sm/semibold" color="text">
            Notifications
          </Text>
          {unreadCount > 0 && (
            <View
              borderRadius={10}
              px="md"
              py="xs"
              alignItems="center"
              justifyContent="center"
              style={{ backgroundColor: theme.colors.blue[800] }}
            >
              <Text textStyle="xxs/semibold" color="white">
                {unreadCount}
              </Text>
            </View>
          )}
        </View>
        <View flexDirection="row" alignItems="center" gap="lg">
          {unreadCount > 0 && onMarkAllRead && (
            <Pressable onPress={onMarkAllRead}>
              <Text textStyle="xs/medium" color="blue">
                Mark all read
              </Text>
            </Pressable>
          )}
          {onClose && (
            <Pressable onPress={onClose} hitSlop={8}>
              <Icon name="close" size={20} color={theme.colors.textMuted} />
            </Pressable>
          )}
        </View>
      </View>

      <View height={1} bg="borderCard" />

      {activities.length === 0 ?
        <View flex={1} alignItems="center" justifyContent="center" py="6xl">
          <Icon name="bellOutline" size={32} color={theme.colors.textMuted} />
          <Text textStyle="sm/regular" color="textMuted" textAlign="center" mt="xl">
            No activity
          </Text>
          <Text textStyle="xs/regular" color="textMuted" textAlign="center" mt="xs">
            {"You're all caught up"}
          </Text>
        </View>
      : <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            paddingBottom: theme.spacing["6xl"],
          }}
        >
          {activities.map((activity, index) => (
            <NotificationItem
              key={activity.id}
              id={activity.id}
              kind={activity.kind}
              summary={activity.summary}
              {...(activity.status && { status: activity.status })}
              createdAt={activity.createdAt}
              read={activity.read}
              {...(activity.subtitle && { subtitle: activity.subtitle })}
              {...(activity.steps && { steps: activity.steps })}
              {...(activity.author && { author: activity.author })}
              onPress={() => onActivityPress?.(activity)}
              onNavigate={() => onNavigate?.(activity)}
              isLast={!hasMore && index === activities.length - 1}
            />
          ))}
          {hasMore && onLoadMore && (
            <Pressable
              onPress={onLoadMore}
              disabled={isLoadingMore}
              style={{
                alignItems: "center",
                paddingVertical: theme.spacing.xl,
              }}
            >
              <Text textStyle="xs/medium" color="blue">
                {isLoadingMore ? "Loading…" : "Load more"}
              </Text>
            </Pressable>
          )}
        </ScrollView>
      }
    </View>
  );
}
